import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Layers, Lock, Plus, X, Download, Archive, Eye, CheckCircle2 } from "lucide-react";

function PriceForm({ onSaved, onCancel }) {
  const [f, setF] = useState({ name: "", description: "", price: "", billing_unit: "day", currency: "USD",
    effective_from: "", effective_to: "", approval_status: "draft", source_reference: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/prices", { ...f, price: f.price === "" ? null : Number(f.price) });
      toast.success("Price entry saved.");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card p-5 space-y-3" data-testid="price-entry-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="label">Service / package name *</label>
          <input className="input" value={f.name} data-testid="price-name-input" onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><label className="label">Approval status</label>
          <select className="input" value={f.approval_status} data-testid="price-status-select" onChange={(e) => setF({ ...f, approval_status: e.target.value })}>
            <option value="draft">Draft — needs Admin confirmation</option><option value="approved">Approved for proposals</option></select></div>
        <div className="sm:col-span-2"><label className="label">Description & inclusions</label>
          <textarea className="input" value={f.description} data-testid="price-description-input" onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        <div><label className="label">Price</label>
          <input type="number" min="0" step="0.01" className="input" value={f.price} placeholder="Blank = needs confirmation" data-testid="price-amount-input"
            onChange={(e) => setF({ ...f, price: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="label">Billing unit</label>
            <select className="input" value={f.billing_unit} data-testid="price-unit-select" onChange={(e) => setF({ ...f, billing_unit: e.target.value })}>
              <option value="day">per day</option><option value="half_day">per half-day</option><option value="hour">per hour</option>
              <option value="session">per session</option><option value="package">per package</option></select></div>
          <div><label className="label">Currency</label>
            <input className="input" value={f.currency} data-testid="price-currency-input" onChange={(e) => setF({ ...f, currency: e.target.value })} /></div>
        </div>
        <div><label className="label">Effective from</label>
          <input type="date" className="input" value={f.effective_from} data-testid="price-from-input" onChange={(e) => setF({ ...f, effective_from: e.target.value })} /></div>
        <div><label className="label">Source reference</label>
          <input className="input" placeholder="e.g. FY26 rate card v1" value={f.source_reference} data-testid="price-source-input"
            onChange={(e) => setF({ ...f, source_reference: e.target.value })} /></div>
      </div>
      <div className="flex gap-2">
        <button className="btn-primary" onClick={save} disabled={busy || !f.name} data-testid="price-save-button">{busy ? "Saving…" : "Save price entry"}</button>
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function TierColumn({ category, title, note, docs, prices, onChange, onAddPrice, showPriceForm, isClientFacing }) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ title: "", description: "", version_label: "", effective_date: "" });

  const upload = async () => {
    const fd = new FormData();
    fd.append("title", meta.title); fd.append("category", category);
    fd.append("description", meta.description); fd.append("version_label", meta.version_label);
    fd.append("effective_date", meta.effective_date);
    if (file) fd.append("file", file);
    try { await api.post("/admin/tier-documents", fd); toast.success("Document added."); setUploadOpen(false); setFile(null); setMeta({ title: "", description: "", version_label: "", effective_date: "" }); onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="card p-6 space-y-4" data-testid={`tier-column-${category}`}>
      <div>
        <h2 className="font-serif font-bold text-xl text-navy flex items-center gap-2">
          {isClientFacing ? <Layers size={18} className="text-terracotta" /> : <Lock size={18} className="text-[#991B1B]" />}
          {title}</h2>
        <p className="text-xs text-[#78716C] mt-1">{note}</p>
      </div>

      {isClientFacing && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="label !mb-0">Structured price entries (used by the Proposal Generator)</span>
            <button className="btn-outline !py-1 !px-3 text-xs" onClick={onAddPrice} data-testid="add-price-button"><Plus size={13} /> Add price</button>
          </div>
          {showPriceForm && <PriceForm onSaved={() => onChange()} onCancel={onAddPrice} />}
          {prices.map((p) => (
            <div key={p.id} className="rounded-lg border border-[#E7E1D4] p-3.5" data-testid={`price-entry-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-bold text-sm text-navy">{p.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-black text-navy">{p.price != null ? `${p.currency} ${p.price.toLocaleString()} / ${p.billing_unit}` : "Needs confirmation"}</span>
                  <span className={p.approval_status === "approved" ? "badge-approved" : "badge-draft"} data-testid={`price-status-${p.id}`}>{p.approval_status}</span>
                </div>
              </div>
              {p.description && <p className="text-xs text-[#57534E] mt-1">{p.description}</p>}
              <div className="text-[11px] text-[#A8A29E] mt-1">
                {p.effective_from && `Effective ${p.effective_from} · `}{p.source_reference && `Source: ${p.source_reference} · `}
                Edits never rewrite existing proposals or invoices.</div>
              <div className="flex gap-1.5 mt-2">
                {p.approval_status !== "approved" && (
                  <button className="btn-ghost !py-1 !px-2 text-xs text-[#065F46]" data-testid={`price-approve-${p.id}`}
                    onClick={async () => { await api.patch(`/admin/prices/${p.id}`, { approval_status: "approved" }); onChange(); toast.success("Price approved for proposal use."); }}>
                    <CheckCircle2 size={13} /> Approve</button>)}
                <button className="btn-ghost !py-1 !px-2 text-xs text-[#991B1B]" data-testid={`price-archive-${p.id}`}
                  onClick={async () => { await api.patch(`/admin/prices/${p.id}`, { archive: true }); onChange(); toast.success("Archived."); }}>
                  <Archive size={13} /> Archive</button>
              </div>
            </div>
          ))}
          {prices.length === 0 && !showPriceForm && <p className="text-sm text-[#78716C]">No price entries yet. Add only Admin-confirmed pricing — nothing is invented.</p>}
        </div>
      )}

      <div className="border-t border-[#F3EFE6] pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="label !mb-0">Documents</span>
          <button className="btn-outline !py-1 !px-3 text-xs" onClick={() => setUploadOpen(!uploadOpen)} data-testid={`tier-upload-toggle-${category}`}>
            <Plus size={13} /> Add document</button>
        </div>
        {uploadOpen && (
          <div className="rounded-lg bg-canvas border border-[#E7E1D4] p-4 space-y-3" data-testid={`tier-upload-form-${category}`}>
            <input className="input" placeholder="Title *" value={meta.title} data-testid={`tier-doc-title-${category}`}
              onChange={(e) => setMeta({ ...meta, title: e.target.value })} />
            <input className="input" placeholder="Description" value={meta.description} data-testid={`tier-doc-desc-${category}`}
              onChange={(e) => setMeta({ ...meta, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <input className="input" placeholder="Version label" value={meta.version_label}
                onChange={(e) => setMeta({ ...meta, version_label: e.target.value })} />
              <input type="date" className="input" value={meta.effective_date}
                onChange={(e) => setMeta({ ...meta, effective_date: e.target.value })} />
            </div>
            <input type="file" className="input !py-2 text-xs" onChange={(e) => setFile(e.target.files?.[0])} data-testid={`tier-doc-file-${category}`} />
            {!isClientFacing && <p className="text-[11px] text-[#991B1B]">Consultant compensation material is internal only and is never used as proposal pricing input.</p>}
            {isClientFacing && <p className="text-[11px] text-[#92400E]">Uploading a pricing PDF does not create or approve prices — enter structured entries above and approve them explicitly.</p>}
            <button className="btn-primary !py-1.5" onClick={upload} disabled={!meta.title || !file} data-testid={`tier-doc-save-${category}`}>Upload</button>
          </div>
        )}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#E7E1D4] px-4 py-2.5" data-testid={`tier-doc-${d.id}`}>
            <div className="min-w-0">
              <span className="text-sm font-bold text-navy">{d.title}</span>
              {d.version_label && <span className="badge-neutral ml-2">{d.version_label}</span>}
              {d.is_sample && <span className="badge-neutral ml-1">Sample</span>}
              {d.description && <div className="text-xs text-[#78716C] truncate">{d.description}</div>}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {d.has_file && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/tier-documents/${d.id}/file`} target="_blank" rel="noreferrer"
                className="btn-ghost !py-1 !px-2 text-xs" data-testid={`tier-doc-download-${d.id}`}><Download size={13} /></a>}
              <button className="btn-ghost !py-1 !px-2 text-xs text-[#991B1B]" data-testid={`tier-doc-archive-${d.id}`}
                onClick={async () => { await api.patch(`/admin/tier-documents/${d.id}`, { archive: true }); onChange(); }}><Archive size={13} /></button>
            </div>
          </div>
        ))}
        {docs.length === 0 && <p className="text-xs text-[#A8A29E]">No documents yet.</p>}
      </div>
    </div>
  );
}

export default function PaymentTiers() {
  const [docs, setDocs] = useState(null);
  const [prices, setPrices] = useState([]);
  const [showPriceForm, setShowPriceForm] = useState(false);

  const load = () => {
    api.get("/admin/tier-documents").then((r) => setDocs(r.data));
    api.get("/admin/prices").then((r) => setPrices(r.data));
  };
  useEffect(() => { load(); }, []);

  if (!docs) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="tiers-loading">Loading payment tiers…</div>;

  return (
    <div className="space-y-6" data-testid="payment-tiers-page">
      <div className="fade-up">
        <p className="eyebrow">Admin only</p>
        <h1 className="section-title text-3xl mt-1">Payment Tiers</h1>
        <p className="mt-3 text-sm text-[#57534E] max-w-2xl">
          Manage client pricing, consultant compensation references, and financial planning documents.
          The two categories below are never mixed: compensation material is internal-only and is never
          used as proposal pricing input. Changes here never alter consultant profile rates, invoice
          calculations, or existing proposal versions.
        </p>
      </div>
      <div className="grid lg:grid-cols-2 gap-6 fade-up-1">
        <TierColumn category="client_pricing" title="Client-Facing Pricing & Service Tiers" isClientFacing
          note="Approved structured entries feed the Proposal Generator. PDFs are reference material only."
          docs={docs.filter((d) => d.category === "client_pricing")} prices={prices} onChange={load}
          onAddPrice={() => setShowPriceForm(!showPriceForm)} showPriceForm={showPriceForm} />
        <TierColumn category="consultant_compensation" title="Consultant Compensation — Internal Only"
          note="Internal reference only. Never visible to consultants, never sent to the proposal generator."
          docs={docs.filter((d) => d.category === "consultant_compensation")} prices={[]} onChange={load}
          onAddPrice={() => {}} showPriceForm={false} />
      </div>
    </div>
  );
}
